import { createSlice } from "@reduxjs/toolkit";
import { normalizeSafeMailFailure } from "../utils/mailFailurePresentation";

const toJobId = (job) => typeof job === 'string' ? job : job?.jobId;
const MAX_RETAINED_FAILURES = 20;
const SAFE_MAILBOX_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SAFE_FAILURE_ID = /^[0-9a-f]{64}$/;

const registerJobs = (state, payload) => {
  const jobIds = [...new Set((payload ?? []).map(toJobId).filter(Boolean))];
  state.total = jobIds.length;
  state.completed = 0;
  state.failed = 0;
  state.jobs = {};
  jobIds.forEach((jobId) => { state.jobs[jobId] = { status: "queued" }; });
};

const emailJobSlice = createSlice({
  name: "emailJobs",
  initialState: {
    total: 0,
    completed: 0,
    failed: 0,
    jobs: {},
    failures: [],
    manualFailureMailboxId: null,
    scheduledFailures: [],
  },
  reducers: {
    addJobs: (state, { payload }) => {
      // A new run starts with a clean slate; resetJobs deliberately does not
      // clear failures, so this is the only place they are dropped.
      state.failures = [];
      state.manualFailureMailboxId = null;
      registerJobs(state, payload);
    },
    addRetryJobs: (state, { payload }) => {
      registerJobs(state, payload);
    },
    clearManualMailFailures: (state) => {
      state.failures = [];
      state.manualFailureMailboxId = null;
    },
    setManualMailFailureMailbox: (state, { payload }) => {
      state.manualFailureMailboxId = typeof payload === 'string' && payload.length > 0
        ? payload
        : null;
    },
    jobDone: (state, { payload }) => {
      if (!payload?.jobId || state.jobs[payload.jobId]?.status !== 'queued') return;
      state.jobs[payload.jobId] = { status: "done", result: payload?.result };
      state.completed++;
    },
    jobFail: (state, { payload }) => {
      if (!payload?.jobId || state.jobs[payload.jobId]?.status !== 'queued') return;
      const safeFailure = normalizeSafeMailFailure(payload?.error);
      state.jobs[payload.jobId] = { status: "failed", error: safeFailure };
      // Counted apart from completed: incrementing completed made a failed
      // batch indistinguishable from a successful one, so the overlay ran to
      // 100% and the user simply found the email untriaged.
      state.failed++;
      state.failures.push(safeFailure);
    },
    retainScheduledMailFailure: (state, { payload }) => {
      if (
        !SAFE_MAILBOX_ID.test(payload?.mailboxId ?? '')
        || !SAFE_FAILURE_ID.test(payload?.failureId ?? '')
      ) return;
      if (!Array.isArray(state.scheduledFailures)) state.scheduledFailures = [];
      if (state.scheduledFailures.some(({ failureId }) => failureId === payload.failureId)) return;
      state.scheduledFailures.push({
        failureId: payload.failureId,
        mailboxId: payload.mailboxId,
        error: normalizeSafeMailFailure(payload.error),
      });
      if (state.scheduledFailures.length > MAX_RETAINED_FAILURES) {
        state.scheduledFailures.splice(
          0,
          state.scheduledFailures.length - MAX_RETAINED_FAILURES,
        );
      }
    },
    clearScheduledMailFailures: (state, { payload }) => {
      if (!SAFE_MAILBOX_ID.test(payload ?? '')) return;
      if (!Array.isArray(state.scheduledFailures)) state.scheduledFailures = [];
      state.scheduledFailures = state.scheduledFailures.filter(
        ({ mailboxId }) => mailboxId !== payload,
      );
    },
    resetJobs: (state) => {
      // Failures outlive the run on purpose. This fires the moment the last
      // job settles, which is also when the overlay unmounts — clearing them
      // here would wipe the only record before anything could render it.
      state.total = 0; state.completed = 0; state.failed = 0; state.jobs = {};
    },
  },
});

export const {
  addJobs,
  addRetryJobs,
  clearManualMailFailures,
  setManualMailFailureMailbox,
  jobDone,
  jobFail,
  retainScheduledMailFailure,
  clearScheduledMailFailures,
  resetJobs
} = emailJobSlice.actions;

export default emailJobSlice.reducer;
